import { Box, SxProps, Theme, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { MAX_THUMBNAILS_WHEN_OVERFLOWING, MAX_VISIBLE_SKUS } from '../../../../constants/ui';
import { ICampaignCard, ICampaignItemsCard } from '../../../../types/pyg';
import { isActivationKey } from '../../../../util/ui';
import {
  CardTitle,
  HeaderRow,
  MultipleItemsContainer,
  OverflowTile,
  SkuTileName,
  StyledActions,
  StyledCard,
  StyledContent,
  TitleColumn,
} from './Card.styles';
import { CardImage, SmallCardImage } from './components';

type CardProps = ICampaignCard | ICampaignItemsCard;

const clickableSx: SxProps<Theme> = { cursor: 'pointer' };

const tileColumnSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 1 };

export const Card: React.FC<CardProps> = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const titleRef = useRef<HTMLDivElement>(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  const {
    sx,
    actions,
    footer,
    badge,
    subtitle,
    isDisabled,
    isSelected,
    campaign,
    title,
    items,
    fallbackTitle,
    onSkuIndexClick,
    onSkuNameClick,
  } = props;
  const { name, items: campaignItems } = campaign ?? {};

  const cardItems = campaignItems ?? items;

  const { image_url, name: itemName, quantity: itemQuantity } = cardItems?.[0] ?? {};

  const isSingleItem = cardItems?.length === 1;
  const isTitledByItem = !name && !title && isSingleItem;
  const cardTitle = name || title || (isSingleItem ? itemName : undefined) || fallbackTitle;

  const isMultipleItemsCount = !!cardItems && cardItems.length > 1;
  const hiddenSkuCount = useMemo(
    () => (cardItems && cardItems.length > MAX_VISIBLE_SKUS ? cardItems.length - MAX_THUMBNAILS_WHEN_OVERFLOWING : 0),
    [cardItems],
  );

  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) {
      return;
    }
    setIsTitleTruncated(el.scrollHeight > el.clientHeight + 1);
  }, [cardTitle]);

  const handleSkuTileClick = useCallback(
    (skuIndex: number) => {
      onSkuIndexClick?.(isMobile ? skuIndex : 0);
    },
    [isMobile, onSkuIndexClick],
  );

  const handleOverflowClick = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      onSkuIndexClick?.(MAX_THUMBNAILS_WHEN_OVERFLOWING);
    },
    [onSkuIndexClick],
  );

  const handleOverflowKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isActivationKey(e)) {
        handleOverflowClick(e);
      }
    },
    [handleOverflowClick],
  );

  const cardImage = useMemo(() => {
    if (!isMultipleItemsCount) {
      return (
        <CardImage
          className="cl-card-media-row"
          imageUrl={image_url ?? ''}
          name={isTitledByItem ? undefined : itemName}
          quantity={itemQuantity}
        />
      );
    }

    const visibleCount = hiddenSkuCount > 0 ? MAX_THUMBNAILS_WHEN_OVERFLOWING : MAX_VISIBLE_SKUS;
    const visibleItems = cardItems?.slice(0, visibleCount) ?? [];

    return (
      // The empty caption row keeps the tile grid as tall as a single-image card, so Select
      // lands on the same line in both.
      <Box sx={tileColumnSx}>
        <MultipleItemsContainer className="cl-card-media-row">
          {visibleItems.map((item, index) => (
            <SmallCardImage
              key={item.id}
              item={item}
              index={index}
              onMediaClick={onSkuIndexClick ? handleSkuTileClick : undefined}
              onNameClick={onSkuNameClick}
            />
          ))}
          {hiddenSkuCount > 0 && (
            <OverflowTile
              role="button"
              tabIndex={0}
              aria-label={`View all ${cardItems?.length ?? 0} items`}
              onClick={handleOverflowClick}
              onKeyDown={handleOverflowKeyDown}
            >
              +{hiddenSkuCount}
            </OverflowTile>
          )}
        </MultipleItemsContainer>
        <SkuTileName aria-hidden>&nbsp;</SkuTileName>
      </Box>
    );
  }, [
    isMultipleItemsCount,
    cardItems,
    image_url,
    isTitledByItem,
    itemName,
    itemQuantity,
    hiddenSkuCount,
    onSkuIndexClick,
    onSkuNameClick,
    handleSkuTileClick,
    handleOverflowClick,
    handleOverflowKeyDown,
  ]);

  const isCardClickable = !!onSkuIndexClick && !isDisabled;

  const handleCardClick = useCallback(() => {
    onSkuIndexClick?.(0);
  }, [onSkuIndexClick]);

  const handleCardKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.target !== e.currentTarget) {
        return;
      }
      if (isActivationKey(e)) {
        e.preventDefault();
        onSkuIndexClick?.(0);
      }
    },
    [onSkuIndexClick],
  );

  const stopActionsPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const titleNode = <CardTitle ref={titleRef}>{cardTitle}</CardTitle>;

  const cardClassName = [isSelected ? 'is-selected' : '', isDisabled ? 'is-disabled' : ''].filter(Boolean).join(' ');

  return (
    // The whole card opens the details sidebar; the actions row stops propagation so Select only selects.
    <StyledCard
      elevation={0}
      className={cardClassName || undefined}
      role={isCardClickable ? 'button' : undefined}
      tabIndex={isCardClickable ? 0 : undefined}
      aria-label={isCardClickable ? `View details for ${cardTitle ?? 'this option'}` : undefined}
      onClick={isCardClickable ? handleCardClick : undefined}
      onKeyDown={isCardClickable ? handleCardKeyDown : undefined}
      sx={isCardClickable ? clickableSx : undefined}
    >
      <StyledContent sx={sx}>
        <HeaderRow>
          <TitleColumn>
            {isTitleTruncated ? (
              <Tooltip title={cardTitle} placement="top" arrow>
                {titleNode}
              </Tooltip>
            ) : (
              titleNode
            )}
            {subtitle}
          </TitleColumn>
          {badge}
        </HeaderRow>
        {cardImage}
        {footer}
      </StyledContent>

      <StyledActions onClick={stopActionsPropagation}>{actions}</StyledActions>
    </StyledCard>
  );
};
